import { Box, Typography, Grid, Chip, Button, Dialog, IconButton } from '@mui/material';

import { InfoCard } from '../../ui/InfoCard';
import { FeatureIcon } from '../../ui/FeatureIcon';
import { ExternalUrls } from '../../../lib/constants';
import { BrandColors } from '@neumind/shared/branding';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';
import { FadeIn } from '../../ui/FadeIn';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { PricingTiers } from '../../../lib/pricing-data';
import { formatPrice } from '../../../lib/pricing-utils';

// Map icon names to components
const IconMap: Record<string, React.ElementType> = {
    headset: HeadsetMicIcon,
    clipboard: AssignmentIcon,
    presentation: GroupsIcon // Using Groups as proxy for presentation/users
};

interface AddOn {
    id: string;
    title: string;
    price: string;
    description: string;
    actionLabel: string;
    iconName: string;
}

interface PricingAddonStripProps {
    data?: readonly AddOn[];
}

export function PricingAddonStrip({ data = [] }: PricingAddonStripProps) {
    const [selectedAddon, setSelectedAddon] = useState<string | null>(null);
    const { currency } = useCurrency();

    // Replace price placeholders with actual formatted prices
    const resolvePricePlaceholders = (priceString: string): string => {
        return priceString
            .replace('{setup_price}', formatPrice(PricingTiers.recoveryArc.setupAddOn[currency], currency))
            .replace('{integration_price}', formatPrice(PricingTiers.recoveryArc.integrationAddOn[currency], currency));
    };

    const handleAction = (addId: string) => {
        if (addId === 'workshops') {
            // Scroll to footer for contact
            const footer = document.getElementById('footer');
            if (footer) {
                footer.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }
        } else {
            // Open soft catch for others
            setSelectedAddon(addId);
        }
    };

    return (
        <>
            <Grid container spacing={4} alignItems="stretch" justifyContent="center">
                {data.map((addon, index) => {
                    const Icon = IconMap[addon.iconName] || AssignmentIcon;

                    return (
                        <Grid item xs={12} md={4} key={addon.id}>
                            <FadeIn delay={index * 100}>
                                <InfoCard
                                    title={addon.title}
                                    titleSize="small"
                                    orientation="horizontal"
                                    icon={<FeatureIcon icon={<Icon />} size="medium" color={BrandColors.primary.main} />}
                                    description={addon.description}
                                    meta={
                                        <Chip
                                            label={resolvePricePlaceholders(addon.price)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                bgcolor: 'grey.50',
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        />
                                    }
                                    cta={{
                                        label: addon.actionLabel,
                                        onClick: () => handleAction(addon.id)
                                    }}

                                    sx={{
                                        alignItems: 'flex-start',
                                        textAlign: 'left'
                                    }}
                                />
                            </FadeIn>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Soft Catch Modal */}
            <Dialog
                open={!!selectedAddon}
                onClose={() => setSelectedAddon(null)}
                PaperProps={{
                    sx: { borderRadius: 4, p: 2, maxWidth: 460 }
                }}
            >
                <Box sx={{ position: 'relative', p: 2, textAlign: 'center' }}>
                    <IconButton
                        onClick={() => setSelectedAddon(null)}
                        sx={{ position: 'absolute', right: -8, top: -8 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3
                        }}
                    >
                        <AssignmentIcon sx={{ fontSize: 28 }} />
                    </Box>

                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Create your account first
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        This service can be booked directly from your Clinician Dashboard once you create your free account.
                    </Typography>

                    <Button
                        component={Link}
                        to="https://dashboard.neumind.io/signup"
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={() => setSelectedAddon(null)}
                        sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, mb: 2 }}
                    >
                        Create Free Account
                    </Button>

                    {selectedAddon === 'integration' && (
                        <Button
                            href={ExternalUrls.calendly}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="text"
                            size="small"
                            sx={{
                                fontSize: '0.95rem',
                                color: 'primary.main',
                                textTransform: 'none',
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                            }}
                        >
                            Want to discuss anything? Book a free scoping call
                        </Button>
                    )}
                </Box>
            </Dialog>
        </>
    );
}
